import type { Terms } from "@gately/validators";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";

import { Avatar } from "@gately/ui/avatar";
import { StatCard } from "@gately/ui/stat-card";
import { StatusBadge } from "@gately/ui/status-badge";
import {
  formatRecurringSchedule,
  formatVisitInstant,
  recurrenceToWeekdays,
  viewerTimeZone,
  wallClockInZone,
} from "@gately/validators";

import type {
  ExpectedGuestInvite,
  ExpectedGuestRow,
  ExpectedGuestStatus,
} from "~/lib/expected-guests";
import type { Membership } from "~/lib/use-me";
import { SyndicDashboard } from "~/components/dashboard/syndic-dashboard";
import { upcomingExpectedGuests } from "~/lib/expected-guests";
import { hostGuestName, PASS_STATUS_LABELS } from "~/lib/invite-status";
import { orpc } from "~/lib/orpc";
import { useMe, useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions, usePermissions } from "~/lib/use-permissions";
import { useSwitchOrg } from "~/lib/use-switch-org";
import { termsForMembership, useTerms } from "~/lib/use-terms";

// Resolved once per module, not per render.
const VIEWER = viewerTimeZone();

export const Route = createFileRoute("/_dashboard/")({
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    // No active org — a multi-résidence member (always the owner, who holds a
    // membership per résidence) hasn't picked one this session, and the
    // sole-membership auto-promote never fires. Route them to pick instead of
    // prefetching org-scoped queries, which 403 "No active organization" and
    // crash the page. Owners get the full résidence console; everyone else
    // picks from their memberships on this page (see HomePage).
    if (!me.activeOrganizationId) {
      if (me.isOwner) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: "/organizations" });
      }
      return;
    }
    // Prefetch so useSuspenseQuery has data ready and never suspends mid-nav.
    // Managers see the analytics overview; plain members see their next
    // expected guests instead, so each branch only prefetches what it needs.
    // Must stay the same predicate the component branches on, or the page
    // suspends on data the loader never fetched.
    await Promise.all(
      resolvePermissions(me).canViewActivity
        ? [
            context.queryClient.ensureQueryData(
              orpc.dashboard.today.queryOptions(),
            ),
            context.queryClient.ensureQueryData(
              orpc.dashboard.health.queryOptions(),
            ),
            context.queryClient.ensureQueryData(
              orpc.stats.setup.queryOptions(),
            ),
            context.queryClient.ensureQueryData(
              orpc.activity.listForResidencePaged.queryOptions({
                input: { limit: 8 },
              }),
            ),
          ]
        : [
            context.queryClient.ensureQueryData(
              orpc.invite.list.queryOptions({ input: { limit: 100 } }),
            ),
            context.queryClient.ensureQueryData(
              orpc.unit.listManageable.queryOptions(),
            ),
          ],
    );
  },
  component: HomePage,
});

function HomePage() {
  const me = useMe();
  const terms = useTerms();
  const perms = usePermissions();

  // No active organization: a member of ≥1 résidence who hasn't selected one
  // this session (owners are redirected to /organizations in the loader). Show
  // a picker rather than the org-scoped overview below, whose queries would
  // 403 "No active organization".
  if (!me.activeOrganizationId) {
    return <PickResidence memberships={me.memberships} />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <h1 className="text-2xl font-bold tracking-tight">
        Bonjour{me.user.name ? `, ${me.user.name}` : ""}.
      </h1>
      <p className="text-muted-foreground mt-1 text-sm">
        {me.activeOrg?.name ?? `Votre ${terms.orgNoun}`}
      </p>

      {perms.canViewActivity ? <SyndicDashboard /> : <MemberHome />}
    </div>
  );
}

function sameDay(a: string | Date, b: Date): boolean {
  return new Date(a).toDateString() === b.toDateString();
}

/** Expected today: dated rows starting today, already-active undated rows,
 *  and recurring access whose weekday pattern includes today. Uses the same
 *  org-local weekday helper as the shared schedule check
 *  (`isOnRecurringSchedule`), so this agrees with /attendus about what's
 *  scheduled today instead of hand-rolling its own getDay()-based map. */
function isExpectedToday(
  row: ExpectedGuestRow,
  now: Date,
  timeZone: string,
): boolean {
  if (row.type === "recurring") {
    if (!row.recurrence) return false;
    const today = wallClockInZone(now, timeZone).weekday;
    return recurrenceToWeekdays(row.recurrence).some((c) => c === today);
  }
  if (!row.validFrom) return row.status === "active";
  return sameDay(row.validFrom, now);
}

// `d` here is always a stored validity instant (row.validFrom) — its
// time-of-day is authored/evaluated in the ORGANIZATION's zone (GATELY-40),
// so display must agree, which the shared zoned formatter guarantees. The
// day-relative label around it (Aujourd'hui/Demain) stays viewer-local —
// that's `isExpectedToday`/`sameDay` above, deliberately unchanged, same as
// expected-guests.ts's dayLabel/startOfDay.
function fmtShortDate(d: string | Date, timeZone: string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    timeZone,
  });
}

/** "Aujourd'hui · 14:00" / "Demain · 09:30" / "20 août · 14:00"; recurring
 *  rows show their weekday pattern instead of a single occurrence. */
function whenLabel(
  row: ExpectedGuestRow,
  now: Date,
  timeZone: string,
): { text: string; secondary: string | null } | null {
  if (row.type === "recurring") {
    if (!row.recurrence) return null;
    // e.g. "Lun, Mer, Ven · 08:00–18:00 · heure de Casablanca" — "" when the
    // recurrence is unparseable, in which case there's nothing more specific
    // to show.
    const schedule = formatRecurringSchedule(
      {
        recurrence: row.recurrence,
        dailyStartMinutes: row.dailyStartMinutes,
        dailyEndMinutes: row.dailyEndMinutes,
      },
      timeZone,
    );
    return schedule ? { text: schedule, secondary: null } : null;
  }
  if (!row.validFrom) return null;
  const from = new Date(row.validFrom);
  const startOf = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(from) - startOf(now)) / 86_400_000);
  const day =
    diffDays === 0
      ? "Aujourd'hui"
      : diffDays === 1
        ? "Demain"
        : fmtShortDate(from, timeZone);
  const t = formatVisitInstant(from, timeZone, { viewerTimeZone: VIEWER });
  return { text: `${day} · ${t.primary}`, secondary: t.secondary };
}

interface RecentVisit {
  guestId: string;
  guestName: string;
  unitLabel: string;
  consumedAt: Date;
}

/** Completed visits (consumed one-time passes), most recent first. Reads the
 *  raw invite rows rather than ExpectedGuestRow, which drops `consumedAt`. */
function recentVisits(
  invites: ExpectedGuestInvite[],
  orgFallback: string,
): RecentVisit[] {
  const visits: RecentVisit[] = [];
  for (const it of invites) {
    for (const g of it.guests) {
      if (!g.consumedAt) continue;
      visits.push({
        guestId: g.id,
        guestName: hostGuestName(g),
        unitLabel: it.number
          ? it.building
            ? `${it.building} ${it.number}`
            : it.number
          : orgFallback,
        consumedAt: new Date(g.consumedAt),
      });
    }
  }
  return visits.sort((a, b) => b.consumedAt.getTime() - a.consumedAt.getTime());
}

function MemberHome() {
  const { data: invites } = useSuspenseQuery(
    orpc.invite.list.queryOptions({ input: { limit: 100 } }),
  );
  const terms = useTerms();
  const timeZone = useOrgTimeZone();
  const now = new Date();

  // The host's own home: their guests by pre-fill or the plain email.
  const expected = upcomingExpectedGuests(
    invites,
    timeZone,
    now,
    terms.orgLabel,
    "plain",
  );
  const nextGuests = expected.slice(0, 5);
  const todayCount = expected.filter((r) =>
    isExpectedToday(r, now, timeZone),
  ).length;
  const pendingCount = expected.filter((r) => r.status === "pending").length;

  const visits = recentVisits(invites, terms.orgLabel);
  const cutoff30d = now.getTime() - 30 * 86_400_000;
  const visits30d = visits.filter(
    (v) => v.consumedAt.getTime() >= cutoff30d,
  ).length;

  return (
    <div className="mt-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Attendus aujourd'hui" value={todayCount} />
        <StatCard label="Visiteurs à venir" value={expected.length} />
        <StatCard
          label="En attente d'approbation"
          value={pendingCount}
          delta={pendingCount > 0 ? "à valider" : undefined}
          deltaTone="neutral"
        />
        <StatCard label="Visites (30 jours)" value={visits30d} />
      </div>

      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white">
          <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3.5">
            <h2 className="text-[14px] font-semibold text-neutral-950">
              Visiteurs attendus
            </h2>
            <Link
              to="/attendus"
              className="text-brand text-[13px] font-medium hover:underline"
            >
              Tout voir
            </Link>
          </div>
          {nextGuests.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-neutral-400">
              <p>Aucun visiteur attendu pour le moment.</p>
              <Link
                to="/inviter"
                className="text-brand mt-2 inline-block text-[13px] font-medium hover:underline"
              >
                Créer une invitation
              </Link>
            </div>
          ) : (
            nextGuests.map((guest) => (
              <NextGuestRow key={guest.guestId} guest={guest} now={now} />
            ))
          )}
        </div>

        <div className="flex flex-col gap-4">
          <TeamCard terms={terms} />

          <div className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white">
            <div className="border-b border-neutral-100 px-5 py-3.5">
              <h2 className="text-[14px] font-semibold text-neutral-950">
                Visites récentes
              </h2>
            </div>
            {visits.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-neutral-400">
                Aucune visite enregistrée.
              </div>
            ) : (
              visits.slice(0, 5).map((v) => (
                <div
                  key={v.guestId}
                  className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-2.5 last:border-0"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar
                      name={v.guestName}
                      className="size-8 shrink-0 text-[11px]"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-neutral-950">
                        {v.guestName}
                      </div>
                      <div className="truncate text-[12px] text-neutral-400">
                        {v.unitLabel}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const t = formatVisitInstant(v.consumedAt, timeZone, {
                      viewerTimeZone: VIEWER,
                    });
                    return (
                      <span
                        className="shrink-0 font-mono text-[12px] text-neutral-500"
                        title={t.secondary ?? undefined}
                      >
                        {fmtShortDate(v.consumedAt, timeZone)} · {t.primary}
                      </span>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The caller's team(s): everyone in the unit(s) they belong to, aggregated
 *  across units for the rare multi-unit member. Any unit member may read the
 *  roster (`foyer.get` allows it); managing it stays on /equipe. */
function TeamCard({ terms }: { terms: Terms }) {
  const { data: units } = useSuspenseQuery(
    orpc.unit.listManageable.queryOptions(),
  );
  const foyerResults = useQueries({
    queries: units.map((u) =>
      orpc.foyer.get.queryOptions({ input: { unitId: u.id } }),
    ),
  });
  const pending = units.length > 0 && foyerResults.some((q) => q.isPending);

  const byUserId = new Map<
    string,
    { name: string; role: "owner" | "admin" | "member" }
  >();
  for (const q of foyerResults) {
    if (!q.data) continue;
    if (q.data.owner && !byUserId.has(q.data.owner.userId)) {
      byUserId.set(q.data.owner.userId, {
        name: q.data.owner.name,
        role: "owner",
      });
    }
    for (const m of q.data.members) {
      if (!byUserId.has(m.userId)) {
        byUserId.set(m.userId, {
          name: m.name,
          role: m.role === "admin" ? "admin" : "member",
        });
      }
    }
  }
  const people = [...byUserId.entries()].map(([userId, p]) => ({
    userId,
    ...p,
  }));
  if (!pending && people.length === 0) return null;

  const ROLE_LABEL = {
    owner: terms.ownerLabel,
    admin: "Admin",
    member: "Membre",
  } as const;

  return (
    <div className="overflow-hidden rounded-[14px] border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3.5">
        <h2 className="text-[14px] font-semibold text-neutral-950">
          {terms.teamNav}
        </h2>
        <Link
          to="/equipe"
          className="text-brand text-[13px] font-medium hover:underline"
        >
          Gérer
        </Link>
      </div>
      {pending ? (
        <div className="px-5 py-8 text-center text-sm text-neutral-400">
          Chargement…
        </div>
      ) : (
        <>
          {people.slice(0, 4).map((p) => (
            <div
              key={p.userId}
              className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-2.5 last:border-0"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Avatar name={p.name} className="size-8 shrink-0 text-[11px]" />
                <div className="truncate text-[13px] font-medium text-neutral-950">
                  {p.name}
                </div>
              </div>
              <StatusBadge
                tone={p.role === "member" ? "info" : "brand"}
                dot={false}
              >
                {ROLE_LABEL[p.role]}
              </StatusBadge>
            </div>
          ))}
          {people.length > 4 ? (
            <div className="px-5 py-2.5 text-[12.5px] text-neutral-400">
              + {people.length - 4} autres
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// upcomingExpectedGuests only ever yields active/upcoming/pending/off_schedule/
// incomplete (see EXPECTED_PASS_STATUSES in @gately/validators), but the map is
// exhaustive over ExpectedGuestStatus so this stays a compile error if that set
// ever grows. Pending gets its own chip so an org's admission gate reads as "not
// yet a done deal" rather than already-confirmed; off_schedule likewise flags
// a recurring guest who is expected, just not right now; incomplete is the
// host's word (« En attente du visiteur »); reception says « Infos non
// complétées » (slice 02).
const NEXT_GUEST_STATUS: Record<
  ExpectedGuestStatus,
  { tone: "success" | "info" | "warning" | "neutral" | "danger"; label: string }
> = {
  active: { tone: "success", label: "Actif" },
  upcoming: { tone: "info", label: "À venir" },
  pending: { tone: "warning", label: "En attente d'approbation" },
  rejected: { tone: "danger", label: "Refusé" },
  expired: { tone: "neutral", label: "Expiré" },
  revoked: { tone: "danger", label: "Révoqué" },
  used: { tone: "neutral", label: "Utilisé" },
  off_schedule: { tone: "warning", label: "Hors créneau" },
  incomplete: { tone: "warning", label: PASS_STATUS_LABELS.incomplete },
};

function NextGuestRow({ guest, now }: { guest: ExpectedGuestRow; now: Date }) {
  // From the cached `me.get`; the venue's clock, not the reader's browser.
  const timeZone = useOrgTimeZone();
  const { tone, label } = NEXT_GUEST_STATUS[guest.status];
  const when = whenLabel(guest, now, timeZone);
  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-3 last:border-0">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar
          name={guest.guestName}
          className="size-9 shrink-0 text-[12px]"
        />
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-medium text-neutral-950">
            {guest.guestName}
          </div>
          <div className="truncate text-[12px] text-neutral-400">
            {guest.unitLabel}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {when ? (
          <span
            className="hidden font-mono text-[12px] text-neutral-500 sm:inline"
            title={when.secondary ?? undefined}
          >
            {when.text}
          </span>
        ) : null}
        <StatusBadge tone={tone}>{label}</StatusBadge>
      </div>
    </div>
  );
}

function PickResidence({ memberships }: { memberships: Membership[] }) {
  const switchOrg = useSwitchOrg();

  // No active org yet, so the vertical is unknown — the wording here has to
  // work for a résidence member and an office employee alike.
  if (memberships.length === 0) {
    return (
      <div className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="text-xl font-semibold tracking-tight">Aucun accès</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Vous n'êtes membre d'aucune résidence ni d'aucun immeuble. Contactez
          votre gestionnaire.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <h1 className="text-2xl font-bold tracking-tight">
        Choisissez votre espace
      </h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Sélectionnez l'espace à gérer pour continuer.
      </p>
      <div className="mt-6 flex flex-col gap-2.5">
        {memberships.map((m) => (
          <button
            key={m.orgId}
            type="button"
            onClick={() => void switchOrg(m.orgId)}
            className="rounded-[14px] border border-neutral-200 bg-white px-5 py-4 text-left transition-colors hover:bg-neutral-50"
          >
            <span className="block truncate text-[14px] font-semibold text-neutral-950">
              {m.orgName}
            </span>
            {/* Each org labelled in ITS OWN vocabulary — the active org's
                vertical is unknown here, and borrowing one would call an
                office building a "Résidence". */}
            <span className="text-muted-foreground block text-[13px]">
              {termsForMembership(m).orgLabel}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
